import { useState, useEffect, ForwardedRef, useImperativeHandle, forwardRef } from 'react';
import { Tabs, Spin, Table } from 'antd';
import { useMemberTimeStore } from 'lowcode/utils/context/memberTimeZone';
import css from '../../index.module.scss';
import styled from './index.module.scss';
import { useColumns } from './useColumns';
import EfModal from '@/components/UI/Modal';
import { NoDataIcon } from '@/components/UI/svg/Ecocredits';
import { BonusTabs, ShareType } from '@/components/ReferralHalloween/index.d';
import { useGlobal } from '@/utils/useGlobal';
import { getPageRewards, getLeaderboard } from '@/service/referral';
import { message } from '@/components/UI';

const { TabPane } = Tabs;

export type BonusModalHandle = {
    show: () => void;
};

interface BonusModalProps {
    isActivity?: boolean; // 是否只显示本次活动的
    planId?: string;
}

const BonusModal = ({ isActivity = true, planId }: BonusModalProps, ref: ForwardedRef<BonusModalHandle>) => {
    const { useLang, useRouter, isMobile } = useGlobal();
    const { locale } = useRouter();
    const { t } = useLang('referral');
    const [active, setActive] = useState<BonusTabs>('referral');
    const [show, setShow] = useState(false);

    const [data, setData] = useState<Record<BonusTabs, any[] | null>>({
        referral: null,
        cashback: null,
        leaderboard: null,
    });

    const { timezoneList } = useMemberTimeStore();

    const columns = useColumns(active, t, timezoneList);

    const getReward = () => {
        const params = {
            awardType: active === 'referral' ? 'REGISTER' : 'FIRST_PURCHASE',
            size: -1,
            shareType: ShareType.SHARER,
        };
        if (isActivity && planId) {
            Object.assign(params, {
                planId,
            });
        }
        getPageRewards(params, locale).then((res) => {
            if (res.code !== '0') return message.error(res.message);

            const obj = { ...data };
            obj[active] = res.data.content;
            setData(obj);
        });
    };

    useImperativeHandle(ref, () => ({
        show: () => setShow(true),
    }));

    useEffect(() => {
        // 查询活动中数据必须planId
        if (isActivity && !planId) return;
        if (active === 'leaderboard') {
            const params = { size: -1 };
            if (isActivity && planId) {
                Object.assign(params, { planId });
            }
            getLeaderboard(params, locale).then((res) => {
                if (res.code !== '0') return message.error(res.message);

                const obj = { ...data };
                obj[active] = res.data.content;
                setData(obj);
            });
            return;
        }
        getReward();
    }, [active, planId]);

    const NoData = () => (
        <div className="no-content">
            <div className="no-content-icon">
                <NoDataIcon />
            </div>
            <p className="no-content-note">{t('no_data')}</p>
        </div>
    );

    return (
        <EfModal
            width={800}
            visible={show}
            open={show}
            className={`${styled.my_bonus_modal} ${css.refer_modal}`}
            onCancel={() => setShow(false)}
            centered={!isMobile}
        >
            <h5 className="title">{t('mybonus.pop_title')}</h5>
            <Tabs className="bonus_tabs" activeKey={active} onChange={(val) => setActive(val as BonusTabs)} centered>
                <TabPane tab={t('mybonus.pop_tab1')} key="referral" />
                <TabPane tab={t('mybonus.pop_tab2')} key="cashback" />
                <TabPane tab={t('mybonus.pop_tab3')} key="leaderboard" />
            </Tabs>
            <div className="modal_content">
                {!data[active] ? (
                    <Spin />
                ) : data[active]?.length === 0 ? (
                    <NoData />
                ) : (
                    <div className="table_box">
                        <Table
                            loading={!data[active]}
                            sticky
                            scroll={{ x: 'max-content', y: '370px' }}
                            className="refer_table"
                            rowClassName={(_row, i) => `${i % 2 === 1 ? 'odd' : 'even'}`}
                            bordered={false}
                            dataSource={data[active] || []}
                            columns={columns}
                            pagination={false}
                        />
                    </div>
                )}
            </div>
        </EfModal>
    );
};

export default forwardRef<BonusModalHandle, BonusModalProps>(BonusModal);
